import { gsap } from "gsap";

// オープニングアニメーション開始前のフラッシュ防止
// アニメーション対象の要素は、以下に記述する
// scss/base/_opening-animation.scss

export function initOpeningAnimation(): void {
  const body = document.body;
  if (body.classList.contains("home")) {
    runHomeAnimation();
  } else if (body.classList.contains("single")) {
    runSingleAnimation();
  } else {
    runPageAnimation();
  }
}

function runHomeAnimation(): void {
  const hero = document.querySelector<HTMLElement>(".p-hero");
  if (!hero) return;

  const heroInner = document.querySelector<HTMLElement>(".p-hero__inner");
  const logo = document.querySelector<HTMLElement>(".l-header__logo");
  const navWrap = document.querySelector<HTMLElement>(".l-header__nav-wrap");
  // .js-modal-open は getBoundingClientRect を stickyAside が参照するため、
  // navWrap 全体に y を適用せず内側の <nav> だけに適用してずれを防ぐ
  const navInner = navWrap?.querySelector<HTMLElement>("nav") ?? null;
  const heroTitle = document.querySelector<HTMLElement>(".p-hero__title");
  const heroDesc = document.querySelector<HTMLElement>(".p-hero__description");
  const lead = document.querySelector<HTMLElement>(".p-lead");
  const slider = document.querySelector<HTMLElement>(".p-slider");
  const main = document.querySelector<HTMLElement>(".l-main");

  // 初期非表示を設定
  gsap.set(hero, { autoAlpha: 0 });
  gsap.set(
    [heroInner, slider, main].filter((el): el is HTMLElement => el !== null),
    { autoAlpha: 0 },
  );
  if (logo) gsap.set(logo, { autoAlpha: 0, y: -15 });
  if (navWrap) gsap.set(navWrap, { autoAlpha: 0 });
  if (navInner) gsap.set(navInner, { y: -15 });
  gsap.set(
    [heroTitle, heroDesc, lead].filter((el): el is HTMLElement => el !== null),
    { autoAlpha: 0, y: 20 },
  );

  // アニメーション完了後に stickyAside の再計算を促す
  const tl = gsap.timeline({
    defaults: { duration: 0.7, ease: "power2.out" },
    onComplete: () => window.dispatchEvent(new Event("resize")),
  });

  // 1. .p-hero 背景カラー
  tl.to(hero, { autoAlpha: 1 });
  // 2. .p-hero__inner 背景画像
  if (heroInner) tl.to(heroInner, { autoAlpha: 1, duration: 1.6 }, "-=0.2");
  // 3. ロゴ
  if (logo) tl.to(logo, { autoAlpha: 1, y: 0 }, "-=0.5");
  // 4. ナビメニュー（コンテナをフェードイン、内側の nav をスライドイン）
  if (navWrap) tl.to(navWrap, { autoAlpha: 1 }, "-=0.3");
  if (navInner) tl.to(navInner, { y: 0 }, "<");
  // 5. ヒーロータイトル
  if (heroTitle) tl.to(heroTitle, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 6. ヒーロー説明文
  if (heroDesc) tl.to(heroDesc, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 7. .p-lead
  if (lead) tl.to(lead, { autoAlpha: 1, y: 0 }, "-=0.1");
  // 8. 以降の全コンテンツ
  const belowEls = [slider, main].filter(
    (el): el is HTMLElement => el !== null,
  );
  if (belowEls.length) tl.to(belowEls, { autoAlpha: 1 }, "-=0.2");
}

function runSingleAnimation(): void {
  const logo = document.querySelector<HTMLElement>(".l-header__logo");
  const navWrap = document.querySelector<HTMLElement>(".l-header__nav-wrap");
  const navInner = navWrap?.querySelector<HTMLElement>("nav") ?? null;
  const breadCrumb = document.querySelector<HTMLElement>(".aioseo-breadcrumbs");
  const singleTitle = document.querySelector<HTMLElement>(".p-single__title");
  const singleMeta = document.querySelector<HTMLElement>(".p-single__meta");
  const main = document.querySelector<HTMLElement>(".l-main__container");

  // 初期非表示を設定
  if (logo) gsap.set(logo, { autoAlpha: 0, y: -15 });
  if (navWrap) gsap.set(navWrap, { autoAlpha: 0 });
  if (navInner) gsap.set(navInner, { y: -15 });
  gsap.set(
    [breadCrumb, singleTitle, singleMeta].filter(
      (el): el is HTMLElement => el !== null,
    ),
    { autoAlpha: 0, y: 20 },
  );
  if (main) gsap.set(main, { autoAlpha: 0 });

  const tl = gsap.timeline({
    defaults: { duration: 0.4, ease: "power2.out" },
    onComplete: () => window.dispatchEvent(new Event("resize")),
  });

  // 1. ロゴ
  if (logo) tl.to(logo, { autoAlpha: 1, y: 0 });
  // 2. ナビメニュー
  if (navWrap) tl.to(navWrap, { autoAlpha: 1 }, "-=0.3");
  if (navInner) tl.to(navInner, { y: 0 }, "<");
  // 3. パンくず
  if (breadCrumb) tl.to(breadCrumb, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 4. 記事タイトル
  if (singleTitle) tl.to(singleTitle, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 5. メタ情報
  if (singleMeta) tl.to(singleMeta, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 6. 以降のコンテンツ
  if (main) tl.to(main, { autoAlpha: 1 }, "+=0.1");
}

function runPageAnimation(): void {
  const logo = document.querySelector<HTMLElement>(".l-header__logo");
  const navWrap = document.querySelector<HTMLElement>(".l-header__nav-wrap");
  const navInner = navWrap?.querySelector<HTMLElement>("nav") ?? null;
  const breadCrumb = document.querySelector<HTMLElement>(".aioseo-breadcrumbs");
  const pageTitle = document.querySelector<HTMLElement>(".p-main__title");
  const pageLead = document.querySelector<HTMLElement>(".p-main__lead");
  const main = document.querySelector<HTMLElement>(".l-main__container");

  // 初期非表示を設定
  if (logo) gsap.set(logo, { autoAlpha: 0, y: -15 });
  if (navWrap) gsap.set(navWrap, { autoAlpha: 0 });
  if (navInner) gsap.set(navInner, { y: -15 });
  gsap.set(
    [breadCrumb, pageTitle, pageLead].filter(
      (el): el is HTMLElement => el !== null,
    ),
    { autoAlpha: 0, y: 20 },
  );
  if (main) gsap.set(main, { autoAlpha: 0 });

  const tl = gsap.timeline({
    defaults: { duration: 0.4, ease: "power2.out" },
    onComplete: () => window.dispatchEvent(new Event("resize")),
  });

  // 1. ロゴ
  if (logo) tl.to(logo, { autoAlpha: 1, y: 0 });
  // 2. ナビメニュー
  if (navWrap) tl.to(navWrap, { autoAlpha: 1 }, "-=0.3");
  if (navInner) tl.to(navInner, { y: 0 }, "<");
  // 3. パンくず
  if (breadCrumb) tl.to(breadCrumb, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 4. ページタイトル
  if (pageTitle) tl.to(pageTitle, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 5. ページリード
  if (pageLead) tl.to(pageLead, { autoAlpha: 1, y: 0 }, "-=0.2");
  // 6. 以降の全コンテンツ
  if (main) tl.to(main, { autoAlpha: 1 }, "+=0.1");
}
